import {
  ApiEndPoint,
  readStoredMeetMode,
  MEET_MODE_SESSION_KEY,
} from "@/services/apiUttiLities";
import axios from "axios";
import { CommanServices } from "@/services/commanServices";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { db } from "../firebase/firebase";
import { loadZoomEmbeddedSdk } from "@/utils/loadZoomSdk";

const CONNECTION = {
  CONNECTING: "Connecting",
  CONNECTED: "Connected",
  RECONNECTING: "Reconnecting",
  CLOSED: "Closed",
  FAIL: "Fail",
};

export const UI_PHASE = {
  CONNECTING: "connecting",
  JOINING: "joining",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  ERROR: "error",
  ENDED: "ended",
};

const REQUIRED_ZOOM_FIELDS = ["meetingId", "signature", "sdkKey"];
const ZOOM_MEETING_STYLE_ID = "zoom-meeting-layout-styles";
const ZOOM_DEBUG = process.env.NODE_ENV !== "production";
const zoomDebug = (...args) => {
  if (ZOOM_DEBUG) console.debug("[Zoom]", ...args);
};

const ZOOM_MEETING_CSS = `
.zoom-meeting-root {
  box-sizing: border-box;
  overflow: hidden;
}
.zoom-meeting-root > div {
  width: 100% !important;
}
.zoom-meeting-root video,
.zoom-meeting-root canvas {
  max-width: 100% !important;
  max-height: 100% !important;
  object-fit: contain !important;
}
.zoom-meeting-root *:has(> video),
.zoom-meeting-root *:has(> canvas) {
  overflow: hidden !important;
}
/* Hide Zoom's built-in minimize — we use a custom minimize flow */
.zoom-meeting-root button[aria-label*="Minimize" i],
.zoom-meeting-root button[title*="Minimize" i] {
  display: none !important;
}
`;

const buildViewSizes = (width, height) => ({
  default: { width: Math.round(width), height: Math.round(height) },
  ribbon: { width: Math.min(300, Math.round(width * 0.28)), height: Math.round(height) },
});

const measureMeetingExtent = (containerEl) => {
  const empty = { contentHeight: 0, videoHeight: 0, videoWidth: 0, videoNode: null };
  if (!containerEl) return empty;
  const base = containerEl.getBoundingClientRect();
  let contentHeight = 0;
  let videoHeight = 0;
  let videoWidth = 0;
  let videoNode = null;
  containerEl.querySelectorAll("*").forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const bottom = rect.bottom - base.top;
    if (bottom > contentHeight) contentHeight = bottom;
    const tag = el.tagName;
    if ((tag === "CANVAS" || tag === "VIDEO") && rect.height > videoHeight) {
      videoHeight = rect.height;
      videoWidth = rect.width;
      videoNode = el;
    }
  });
  return { contentHeight, videoHeight, videoWidth, videoNode };
};

const validateZoomCredentials = (data) => {
  const credentials = data || {};
  const missing = REQUIRED_ZOOM_FIELDS.filter((field) => {
    const value = credentials[field];
    return value === undefined || value === null || String(value).trim() === "";
  });
  return { valid: missing.length === 0, missing, credentials };
};

const waitForContainerLayout = (container) =>
  new Promise((resolve) => {
    const measure = () => {
      if (container?.offsetWidth > 0 && container?.offsetHeight > 0) {
        resolve();
        return;
      }
      requestAnimationFrame(measure);
    };
    requestAnimationFrame(measure);
  });

const waitForContainerRef = (getContainer, maxFrames = 120) =>
  new Promise((resolve, reject) => {
    let frames = 0;
    const tick = () => {
      const el = getContainer();
      if (el) {
        resolve(el);
        return;
      }
      frames += 1;  
      if (frames >= maxFrames) {
        reject(new Error("Zoom container ref timeout"));
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });

const waitForPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

const waitForSessionContainerLayout = (container, minSize = 100, maxFrames = 120) =>
  new Promise((resolve, reject) => {
    let frames = 0;
    const measure = () => {
      if (container?.offsetWidth >= minSize && container?.offsetHeight >= minSize) {
        resolve();
        return;
      }
      frames += 1;
      if (frames >= maxFrames) {
        reject(new Error("Zoom container layout timeout"));
        return;
      }
      requestAnimationFrame(measure);
    };
    requestAnimationFrame(measure);
  });

const isInvalid = (val) => !val || val === "null" || val === "undefined";

const titleCase = (str) => str.replace(/\b\w/g, (char) => char.toUpperCase());

const ZoomMeetingContext = createContext(null);

export function useZoomMeeting() {
  const ctx = useContext(ZoomMeetingContext);
  if (!ctx) throw new Error("useZoomMeeting must be used within ZoomMeetingProvider");
  return ctx;
}

export function ZoomMeetingProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);

  const mountedRef = useRef(true);
  const clientRef = useRef(null);
  const zoomSdkRef = useRef(null);
  const connectionHandlerRef = useRef(null);
  const endedRef = useRef(false);
  const cleanupDoneRef = useRef(false);
  const initStartedRef = useRef(false);
  const joinStartedRef = useRef(false);
  const meetingNotifiedRef = useRef(false);
  const leaveMeetingRef = useRef(() => {});
  const fitFrameRef = useRef(null);
  const layoutEventsRef = useRef(null);
  const sessionParamsRef = useRef(null);
  const sessionStartingRef = useRef(false);

  const [session, setSession] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [uiPhase, setUiPhase] = useState(UI_PHASE.CONNECTING);
  const [statusMessage, setStatusMessage] = useState("Connecting to meeting...");
  const [errorMessage, setErrorMessage] = useState(null);

  const safeSetUi = useCallback((phase, message, error = null) => {
    if (!mountedRef.current) return;
    setUiPhase(phase);
    if (message !== undefined) setStatusMessage(message);
    if (error !== undefined) setErrorMessage(error);
  }, []);

  const failWithError = useCallback((userMessage, logContext, err) => {
    console.error(`[Zoom] ${logContext}`, err || "");
    safeSetUi(UI_PHASE.ERROR, null, userMessage);
  }, [safeSetUi]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (document.getElementById(ZOOM_MEETING_STYLE_ID)) return undefined;
    const style = document.createElement("style");
    style.id = ZOOM_MEETING_STYLE_ID;
    style.textContent = ZOOM_MEETING_CSS;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);

  const buildMeetsUrl = useCallback((params) => {
    const q = new URLSearchParams();
    if (params.incidentId) q.set("incidentId", params.incidentId);
    if (params.meeting_id) q.set("meeting_id", params.meeting_id);
    if (params.callId) q.set("callId", params.callId);
    if (params.meetMode) q.set("meetMode", params.meetMode);
    return `/meets?${q.toString()}`;
  }, []);

  const navigateAfterLeave = useCallback((params) => {
    if (isInvalid(params?.incidentId)) {
      navigate("/dashboard");
    } else {
      navigate(`/incident-details/${params.incidentId}/chat`, {
        state: { restoreConvoRoomId: params.roomName },
      });
    }
  }, [navigate]);

  const sendMeetingHandler = useCallback(async (params) => {
    if (isInvalid(params.incidentId)) return;
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    const { roomName, isAudioOnly, incident, meeting_id: meetingIdParam } = params;
    const meetingId = uuidv4();

    try {
      const isDm = roomName.startsWith("DM-");
      const isGrp = roomName.startsWith("GRP-");

      if (isDm || isGrp) {
        const socketService = (await import("../services/socketService")).default;
        const myId = currentUser?.id || currentUser?._id;
        const myName = currentUser?.full_name || currentUser?.name || "Teammate";

        if (isDm) {
          const otherId = roomName
            .replace(`DM-${params.incidentId}-`, "")
            .split("_")
            .find((id) => String(id) !== String(myId));
          socketService.emit("send_dm_message", {
            room: roomName,
            roomId: roomName,
            senderId: myId,
            senderName: myName,
            receiverId: otherId,
            recipientId: otherId,
            text: isAudioOnly ? "📞 Join voice call" : "🎥 Join video session",
            type: isAudioOnly ? "VOICE_CALL" : "VIDEO_CALL",
            meeting_id: roomName,
            incidentId: params.incidentId,
            createdAt: new Date().toISOString(),
          });
        } else if (isGrp) {
          const participantIds = roomName.replace(`GRP-${params.incidentId}-`, "").split("_");
          socketService.emit("send_group_message", {
            room: roomName,
            roomId: roomName,
            senderId: myId,
            senderName: myName,
            text: isAudioOnly ? "📞 Join voice call" : "🎥 Join video session",
            type: isAudioOnly ? "VOICE_CALL" : "VIDEO_CALL",
            meeting_id: roomName,
            incidentId: params.incidentId,
            participants: participantIds,
            is_group: true,
            createdAt: new Date().toISOString(),
          });
        }
      } else {
        await CommanServices.addChatHistory({
          incidentId: params.incidentId,
          title: incident?.incident_type ? titleCase(incident.incident_type) : "Unknown Incident",
          type: isAudioOnly ? "VOICE_CALL" : "VIDEO_CALL",
          user_id: currentUser.id || currentUser?._id,
        });

        if (!meetingIdParam) {
          await addDoc(collection(db, "incidents", params.incidentId, "messages"), {
            text: "",
            username: currentUser.full_name || "Anonymous",
            email: currentUser.email,
            userId: currentUser.id || currentUser?._id,
            createdAt: serverTimestamp(),
            type: isAudioOnly ? "VOICE_CALL" : "VIDEO_CALL",
            meeting_id: meetingId,
          });
        }
      }
    } catch (error) {
      console.error("[Zoom] sendMeetingHandler failed", error);
    }
  }, []);

  const cleanupZoom = useCallback(async ({ leave = true, destroy = true, force = false } = {}) => {
    if (cleanupDoneRef.current && !force) return;
    cleanupDoneRef.current = true;

    const client = clientRef.current;
    const handler = connectionHandlerRef.current;

    if (client && handler) {
      try {
        client.off("connection-change", handler);
      } catch (e) {
        console.warn("[Zoom] Failed to remove connection-change listener", e);
      }
    }

    const layoutEvents = layoutEventsRef.current;
    layoutEvents?.unregister?.();

    if (client && leave) {
      try {
        await client.leaveMeeting();
      } catch (e) {
        console.warn("[Zoom] leaveMeeting failed during cleanup", e);
      }
    }

    if (destroy) {
      try {
        zoomSdkRef.current?.destroyClient();
      } catch (e) {
        console.warn("[Zoom] destroyClient failed during cleanup", e);
      }
    }

    clientRef.current = null;
    connectionHandlerRef.current = null;
  }, []);

  const resetSessionRefs = useCallback(() => {
    endedRef.current = false;
    cleanupDoneRef.current = false;
    initStartedRef.current = false;
    joinStartedRef.current = false;
    meetingNotifiedRef.current = false;
    clientRef.current = null;
    connectionHandlerRef.current = null;
  }, []);

  const leaveMeeting = useCallback(async (reason = "user") => {
    if (endedRef.current) return;
    endedRef.current = true;

    const params = sessionParamsRef.current;
    const callId = params?.callId;

    if (callId && !isInvalid(callId)) {
      let participantCount = 0;
      try {
        participantCount = (clientRef.current?.getAttendeeslist?.() || []).length;
      } catch (e) {
        console.warn("[Zoom] Could not get participant count", e);
      }
      if (participantCount <= 1) {
        try {
          await axios.patch(
            `${ApiEndPoint}/api/calls/${callId}/status`,
            {},
            {
              params: { status: "ended" },
              headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
            }
          );
        } catch (err) {
          console.error("[Zoom] Failed to update call status to ended", err);
        }
      }
    }

    await cleanupZoom({ leave: true, destroy: true, force: true });

    if (!mountedRef.current) return;

    setSession(null);
    setIsMinimized(false);
    sessionParamsRef.current = null;

    if (reason === "closed") {
      safeSetUi(UI_PHASE.ENDED, "Meeting ended");
    }

    if (params) navigateAfterLeave(params);
  }, [cleanupZoom, navigateAfterLeave, safeSetUi]);

  leaveMeetingRef.current = leaveMeeting;

  const FIT_TARGETS = () => {
    const container = containerRef.current;
    return container ? Array.from(container.children) : [];
  };

  const clearMeetingScale = () => {
    FIT_TARGETS().forEach((el) => {
      el.style.transform = "none";
      el.style.transformOrigin = "top center";
    });
  };

  const fitMeetingToViewport = useCallback((attempt = 0) => {
    const container = containerRef.current;
    if (!container || !sessionParamsRef.current || !document.body.contains(container)) return;
    const targets = FIT_TARGETS();
    if (!targets.length) {
      if (attempt < 12) {
        fitFrameRef.current = window.setTimeout(() => fitMeetingToViewport(attempt + 1), 250);
      }
      return;
    }

    clearMeetingScale();
    const clientHeight = container.clientHeight;
    const { contentHeight } = measureMeetingExtent(container);
    const overflow = contentHeight - clientHeight;

    if (!contentHeight) {
      if (attempt < 12) {
        fitFrameRef.current = window.setTimeout(() => fitMeetingToViewport(attempt + 1), 250);
      }
      return;
    }

    if (overflow > 1) {
      const scale = Math.max(clientHeight / contentHeight, 0.1);
      targets.forEach((el) => {
        el.style.transformOrigin = "top center";
        el.style.transform = `scale(${scale})`;
      });
    }
  }, []);

  const relayoutVideo = useCallback(() => {
    const container = containerRef.current;
    const client = clientRef.current;
    if (!container || !sessionParamsRef.current) return;
    if (!document.body.contains(container)) return;
    if (fitFrameRef.current) {
      clearTimeout(fitFrameRef.current);
      fitFrameRef.current = null;
    }
    clearMeetingScale();
    try {
      client?.updateVideoOptions?.({
        viewSizes: buildViewSizes(container.clientWidth, container.clientHeight),
      });
    } catch (e) {
      /* ignore */
    }
    fitFrameRef.current = window.setTimeout(() => fitMeetingToViewport(0), 200);
  }, [fitMeetingToViewport]);

  const startSession = useCallback(async (params) => {
    resetSessionRefs();
    sessionParamsRef.current = params;
    setIsMinimized(false);
    safeSetUi(UI_PHASE.CONNECTING, "Connecting to meeting...");
    // Apply session first so container styles expand before SDK init measures layout.
    setSession(params);

    if (params.meetModeFromUrl) {
      try {
        sessionStorage.setItem(MEET_MODE_SESSION_KEY, String(params.meetModeFromUrl).toLowerCase());
      } catch (e) {
        /* ignore */
      }
    }

    const userData = JSON.parse(localStorage.getItem("user") || "{}");

    let zoomContainer;
    try {
      await waitForPaint();
      zoomContainer = await waitForContainerRef(() => containerRef.current);
      await waitForSessionContainerLayout(zoomContainer);
    } catch (error) {
      failWithError(
        "Unable to start meeting. Please refresh and try again.",
        "Container not available",
        error
      );
      setSession(null);
      sessionParamsRef.current = null;
      return;
    }

    if (!mountedRef.current || !document.body.contains(zoomContainer)) {
      setSession(null);
      sessionParamsRef.current = null;
      return;
    }

    let zoomCredentials;
    try {
      const zoomRes = await axios.get(`${ApiEndPoint}/api/calls/zoom/${params.roomName}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
      });
      const validation = validateZoomCredentials(zoomRes.data?.data);
      if (!validation.valid) {
        failWithError(
          "Unable to join meeting. Meeting credentials are incomplete.",
          `Missing required fields: ${validation.missing.join(", ")}`
        );
        return;
      }
      zoomCredentials = validation.credentials;
    } catch (error) {
      const isNetwork = !error?.response;
      failWithError(
        isNetwork
          ? "Connection failed. Check your network and try again."
          : "Unable to load meeting credentials. Please try again.",
        "Backend credential fetch failed",
        error
      );
      return;
    }

    if (!mountedRef.current) return;

    let ZoomMtgEmbedded;
    try {
      ZoomMtgEmbedded = await loadZoomEmbeddedSdk();
      zoomSdkRef.current = ZoomMtgEmbedded;
    } catch (error) {
      failWithError("Failed to load meeting SDK. Please refresh and try again.", "Zoom SDK load failed", error);
      return;
    }

    try {
      ZoomMtgEmbedded.destroyClient();
    } catch (e) {
      console.warn("[Zoom] Pre-init destroyClient failed", e);
    }

    const client = ZoomMtgEmbedded.createClient();
    clientRef.current = client;

    const onConnectionChange = async (payload) => {
      const state = payload?.state;
      console.log("[Zoom] connection-change:", state, payload);

      if (state === CONNECTION.CONNECTING) {
        safeSetUi(UI_PHASE.JOINING, "Joining meeting...");
        return;
      }
      if (state === CONNECTION.RECONNECTING) {
        safeSetUi(UI_PHASE.RECONNECTING, "Reconnecting...");
        return;
      }
      if (state === CONNECTION.CONNECTED) {
        safeSetUi(UI_PHASE.CONNECTED, "");
        if (!meetingNotifiedRef.current) {
          meetingNotifiedRef.current = true;
          sendMeetingHandler(params);
        }
        relayoutVideo();
        return;
      }
      if (state === CONNECTION.CLOSED || state === CONNECTION.FAIL) {
        await leaveMeeting(state === CONNECTION.FAIL ? "error" : "closed");
      }
    };

    connectionHandlerRef.current = onConnectionChange;
    safeSetUi(UI_PHASE.JOINING, "Joining meeting...");

    if (!document.body.contains(zoomContainer)) {
      failWithError("Unable to start meeting. Please refresh and try again.", "Container detached from DOM");
      setSession(null);
      sessionParamsRef.current = null;
      return;
    }

    try {
      await client.init({
        zoomAppRoot: zoomContainer,
        language: "en-US",
        patchJsMedia: true,
        leaveOnPageUnload: true,
        customize: {
          video: {
            isResizable: false,
            defaultViewType: "gallery",
            viewSizes: buildViewSizes(zoomContainer.clientWidth, zoomContainer.clientHeight),
            popper: { disableDraggable: true },
          },
          toolbar: {
            buttons: [
              {
                text: "Leave Meeting",
                className: "zoom-custom-leave-btn",
                onClick: () => {
                  void leaveMeetingRef.current("user");
                },
              },
            ],
          },
        },
      });
    } catch (error) {
      failWithError("Failed to initialize meeting. Please refresh and try again.", "client.init failed", error);
      void cleanupZoom({ leave: true, destroy: true, force: true });
      setSession(null);
      return;
    }

    const LAYOUT_EVENTS = [
      "peer-video-state-change",
      "user-added",
      "user-removed",
      "active-speaker",
      "video-active-change",
    ];
    const onLayoutEvent = () => relayoutVideo();
    const registerLayoutEvents = () => {
      if (!client?.on) return;
      LAYOUT_EVENTS.forEach((evt) => {
        try {
          client.on(evt, onLayoutEvent);
        } catch (e) {
          /* ignore */
        }
      });
    };
    const unregisterLayoutEvents = () => {
      if (!client?.off) return;
      LAYOUT_EVENTS.forEach((evt) => {
        try {
          client.off(evt, onLayoutEvent);
        } catch (e) {
          /* ignore */
        }
      });
    };
    layoutEventsRef.current = { register: registerLayoutEvents, unregister: unregisterLayoutEvents };

    try {
      client.on("connection-change", onConnectionChange);
      registerLayoutEvents();
    } catch (error) {
      failWithError(
        "Failed to initialize meeting events. Please refresh and try again.",
        "client.on(connection-change) failed",
        error
      );
      void cleanupZoom({ leave: true, destroy: true, force: true });
      setSession(null);
      return;
    }

    if (!mountedRef.current) {
      void cleanupZoom({ leave: true, destroy: true, force: true });
      setSession(null);
      return;
    }

    if (joinStartedRef.current) return;
    joinStartedRef.current = true;
    initStartedRef.current = true;

    const { meetingId: zoomMeetingId, signature, sdkKey, passWord } = zoomCredentials;
    try {
      await client.join({
        signature,
        sdkKey,
        meetingNumber: String(zoomMeetingId),
        userName: userData?.full_name || "Guest",
        password: passWord || "",
        userEmail: userData?.email || "",
      });
    } catch (error) {
      failWithError(
        "Failed to join meeting. Please check your connection and try again.",
        "client.join failed",
        error
      );
      void cleanupZoom({ leave: true, destroy: true, force: true });
      setSession(null);
    }
  }, [
    cleanupZoom,
    failWithError,
    leaveMeeting,
    relayoutVideo,
    resetSessionRefs,
    safeSetUi,
    sendMeetingHandler,
  ]);

  const ensureSession = useCallback(
    async (params) => {
      const active = sessionParamsRef.current;
      if (active?.roomName === params.roomName && clientRef.current && !endedRef.current) {
        if (active.incidentId !== params.incidentId || active.callId !== params.callId) {
          sessionParamsRef.current = { ...active, ...params };
          setSession((prev) => (prev ? { ...prev, ...params } : params));
        }
        return;
      }
      if (sessionStartingRef.current) return;
      sessionStartingRef.current = true;
      try {
        if (clientRef.current) {
          await cleanupZoom({ leave: true, destroy: true, force: true });
        }
        await startSession(params);
      } finally {
        sessionStartingRef.current = false;
      }
    },
    [cleanupZoom, startSession]
  );

  const minimizeMeeting = useCallback(() => {
    const params = sessionParamsRef.current;
    if (!params || endedRef.current) return;
    if (uiPhase !== UI_PHASE.CONNECTED && uiPhase !== UI_PHASE.RECONNECTING) return;

    zoomDebug("minimizeMeeting", { roomName: params.roomName });
    setIsMinimized(true);
    relayoutVideo();
    navigateAfterLeave(params);
  }, [navigateAfterLeave, relayoutVideo, uiPhase]);

  const expandMeeting = useCallback((options = {}) => {
    const params = sessionParamsRef.current;
    if (!params || endedRef.current) return;
    const skipNavigate = options.skipNavigate === true;

    zoomDebug("expandMeeting", { roomName: params.roomName, skipNavigate, path: location.pathname });
    setIsMinimized(false);

    if (!skipNavigate && location.pathname !== "/meets") {
      navigate(buildMeetsUrl(params));
    }
    requestAnimationFrame(() => relayoutVideo());
  }, [buildMeetsUrl, location.pathname, navigate, relayoutVideo]);

  useEffect(() => {
    zoomDebug("isMinimized changed", isMinimized);
  }, [isMinimized]);

  useEffect(() => {
    if (!session) return undefined;

    const handleResize = () => relayoutVideo();
    window.addEventListener("resize", handleResize);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver?.disconnect();
      if (fitFrameRef.current) clearTimeout(fitFrameRef.current);
    };
  }, [session, relayoutVideo]);

  useEffect(() => {
    if (isMinimized && session) {
      relayoutVideo();
    }
  }, [isMinimized, session, relayoutVideo]);

  useEffect(() => {
    return () => {
      if (fitFrameRef.current) clearTimeout(fitFrameRef.current);
      void cleanupZoom({ leave: true, destroy: true, force: true });
    };
  }, [cleanupZoom]);

  const containerStyle = useMemo(() => {
    if (!session) {
      return {
        position: "fixed",
        left: -10000,
        top: 0,
        width: 1,
        height: 1,
        overflow: "hidden",
        opacity: 0,
        visibility: "hidden",
        pointerEvents: "none",
        zIndex: -1,
      };
    }
    if (isMinimized) {
      return {
        position: "fixed",
        bottom: 72,
        right: 16,
        width: 320,
        height: 180,
        zIndex: 9998,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        background: "#111",
        pointerEvents: "none",
      };
    }
    return {
      position: "fixed",
      inset: 0,
      width: "100vw",
      height: "100dvh",
      maxHeight: "100vh",
      zIndex: 9999,
      background: "#000",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      overscrollBehavior: "none",
    };
  }, [session, isMinimized]);

  const value = useMemo(
    () => ({
      session,
      isMinimized,
      uiPhase,
      statusMessage,
      errorMessage,
      ensureSession,
      minimizeMeeting,
      expandMeeting,
      leaveMeeting,
      canMinimize: uiPhase === UI_PHASE.CONNECTED || uiPhase === UI_PHASE.RECONNECTING,
      isActive: Boolean(session),
    }),
    [
      session,
      isMinimized,
      uiPhase,
      statusMessage,
      errorMessage,
      ensureSession,
      minimizeMeeting,
      expandMeeting,
      leaveMeeting,
    ]
  );

  return (
    <ZoomMeetingContext.Provider value={value}>
      {children}
      <div
        ref={containerRef}
        className="zoom-meeting-root"
        style={containerStyle}
        aria-hidden={!session || isMinimized}
      />
    </ZoomMeetingContext.Provider>
  );
}

export function parseZoomMeetingParams(search, { meetMode, incidents }) {
  const params = new URLSearchParams(search);
  const incidentId = params.get("incidentId");
  const meeting_id = params.get("meeting_id");
  const callId = params.get("callId");
  const meetModeFromUrl = params.get("meetMode");

  const incident = incidents?.find((inc) => inc.id === incidentId);
  const meetModeStored = readStoredMeetMode();
  const mode = String(meetModeFromUrl || meetModeStored || meetMode || "voice").toLowerCase();
  const isAudioOnly = mode === "voice" || mode === "audio";

  const roomName = !isInvalid(meeting_id)
    ? meeting_id
    : !isInvalid(incidentId)
      ? `INC-${incidentId}`
      : `ROOM-${uuidv4().split("-")[0]}`;

  return {
    incidentId,
    meeting_id,
    callId,
    meetModeFromUrl,
    meetMode: mode,
    isAudioOnly,
    roomName,
    incident,
  };
}
